import { F_MapMarkerWallFragment, MapMarkerWallStructureType } from '__generated__v2/graphql';
import { EmptyLayer } from 'pages/special/OsloDesignFair/map/markers/view/EmptyLayer';
import { transformLineCoordinatesIntoPolygonCoordinates } from 'pages/special/OsloDesignFair/map/markers/view/WallMarker/MultiLineWall';
import {
    GroupWallMarkerDataProps,
    WallSegment
} from 'pages/special/OsloDesignFair/map/markers/view/WallMarker/WallSegment';

interface Props {
    id: string;
    markers: F_MapMarkerWallFragment[];
    beforeId: string | undefined;
}

export const GroupWallMarkers = (props: Props): JSX.Element => {
    const markers = props.markers.filter((e) => !e.isHidden);

    const sourceIds = {
        nonSelectable: 'wall_group|' + props.id,
        selectable: 'wall_group|' + props.id + '|clickable'
    };

    const layerIds = {
        layer: 'wall_group|' + props.id + '|layer',
        selectable: 'wall_group|' + props.id + '|clickable',
        last: 'wall_group|' + props.id + '|last'
    };

    const beforeIds = {
        layer: props.beforeId,
        selectable: layerIds.layer,
        last: layerIds.selectable
    };

    function toMarkerData(marker: F_MapMarkerWallFragment) {
        return {
            wallProps: {
                color:
                    marker.structureType === MapMarkerWallStructureType.Wall
                        ? marker.wallColor
                        : marker.fillColor,
                height:
                    marker.structureType === MapMarkerWallStructureType.Wall
                        ? marker.wallHeight
                        : marker.fillHeight,
                baseHeight:
                    marker.structureType === MapMarkerWallStructureType.Wall
                        ? marker.wallBaseHeight
                        : marker.fillBaseHeight
            },
            markerId: marker.id,
            selectable: marker.selectable ?? false,
            orderIndex: 0,
            visible: marker.isHidden ? false : true
        };
    }

    function reduceMarkers(markersToReduce: F_MapMarkerWallFragment[]) {
        return markersToReduce.reduce((acc, marker) => {
            const markerData = toMarkerData(marker);
            if (
                marker.structureType === MapMarkerWallStructureType.Wall ||
                marker.transformedCoordinates.length < 3
            ) {
                const rectangleCoords = transformLineCoordinatesIntoPolygonCoordinates(
                    marker.transformedCoordinates,
                    marker.wallWidth
                );
                return [
                    ...acc,
                    ...rectangleCoords.map((coordinates) => ({
                        ...markerData,
                        coordinates
                    }))
                ];
            }

            const closedCoordinates = [
                ...marker.transformedCoordinates,
                marker.transformedCoordinates[0]
            ].filter(Boolean);
            return [...acc, { ...markerData, coordinates: closedCoordinates }];
        }, [] as GroupWallMarkerDataProps[]);
    }

    return (
        <>
            <WallSegment
                sourceId={sourceIds.nonSelectable}
                layerId={layerIds.layer}
                beforeId={beforeIds.layer}
                markerData={reduceMarkers(markers.filter((e) => !e.selectable))}
                opacity={1}
            />
            <WallSegment
                sourceId={sourceIds.selectable}
                layerId={layerIds.selectable}
                beforeId={beforeIds.selectable}
                markerData={reduceMarkers(markers.filter((e) => e.selectable))}
                opacity={1}
            />
            <EmptyLayer id={layerIds.last} beforeId={beforeIds.last} />
        </>
    );
};
